import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import OrderDetailModal from '@/components/OrderDetailModal';
import { Search, Eye, RefreshCw, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [refundAction, setRefundAction] = useState('approve');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const url = statusFilter 
        ? `${BACKEND_URL}/api/admin/orders?status=${statusFilter}`
        : `${BACKEND_URL}/api/admin/orders`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        
        // Fetch ALL refunds once
        const refundsRes = await fetch(
          `${BACKEND_URL}/api/orders/admin/refunds`,
          { credentials: 'include' }
        );
        
        let allRefunds = [];
        if (refundsRes.ok) {
          allRefunds = await refundsRes.json();
        }
        
        // Map refunds to orders
        const ordersWithRefunds = data.map(order => {
          if (order.refund_status) {
            // Find the refund for this order
            const refund = allRefunds.find(r => r.order_id === order.id);
            if (refund) {
              return { ...order, refund };
            }
          }
          return order;
        });
        
        console.log('Orders with refunds:', ordersWithRefunds); // Debug log
        setOrders(ordersWithRefunds);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const closeOrderDetail = () => {
    setSelectedOrder(null);
    setShowDetailModal(false);
  };

  const openRefundModal = (refund) => {
    console.log('Opening refund modal with:', refund); // Debug log
    setSelectedRefund(refund);
    setRefundModal(true);
    setRefundAction('approve');
    setAdminNotes('');
  };

  const closeRefundModal = () => {
    setSelectedRefund(null);
    setRefundModal(false);
    setAdminNotes('');
  };

  const handleProcessRefund = async () => {
    if (!selectedRefund) return;

    setProcessing(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/orders/admin/refunds/process`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            refund_id: selectedRefund.id,
            action: refundAction,
            admin_notes: adminNotes
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(
          refundAction === 'approve' 
            ? 'Refund processed successfully!' 
            : 'Refund request rejected'
        );
        closeRefundModal();
        fetchOrders(); // Refresh orders
      } else {
        toast.error(data.detail || 'Failed to process refund');
      }
    } catch (error) {
      console.error('Refund processing error:', error);
      toast.error('Failed to process refund');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'refunded': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getRefundBadge = (refundStatus) => {
    if (!refundStatus) return null;

    const badges = {
      requested: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-400', label: '🔔 Refund Requested' },
      approved: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-400', label: '✓ Approved' },
      completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-400', label: '✓ Refunded' },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-400', label: '✗ Rejected' },
      failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-400', label: '✗ Failed' }
    };

    const badge = badges[refundStatus];
    if (!badge) return null;

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Orders Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage customer orders and refund requests</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>

          <button
            onClick={fetchOrders}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Order ID</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Customer</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Items</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Total</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Date</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">No orders found</td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/50">
                      <td className="py-3 px-4 font-mono text-sm text-gray-600 dark:text-gray-400"title={order.id}>
                        {order.id}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 dark:text-white">{order.user?.name}</div>
                        <div className="text-sm text-gray-500">{order.user?.email}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {order.items?.length || 0} items
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white font-medium">
                        ₹{order.total_amount?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold inline-block ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                          {getRefundBadge(order.refund_status)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openOrderDetail(order)}
                            className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          
                          {order.refund_status === 'requested' && order.refund && (
                            <button
                              onClick={() => openRefundModal(order.refund)}
                              className="p-2 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition animate-pulse"
                              title="Process Refund Request"
                            >
                              <RefreshCw size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={closeOrderDetail}
          onProcessRefund={openRefundModal}
        />
      )}

      {/* Refund Processing Modal */}
      {refundModal && selectedRefund && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Process Refund</h2>
              <button
                onClick={closeRefundModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                disabled={processing}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-900 dark:text-orange-400 mb-2">Refund Details</h3>
                <div className="space-y-1 text-sm">
                  <p className="text-orange-700 dark:text-orange-300">
                    <span className="font-medium">Amount:</span> ₹{selectedRefund.amount?.toLocaleString()}
                  </p>
                  <p className="text-orange-700 dark:text-orange-300">
                    <span className="font-medium">Order ID:</span> {selectedRefund.order_id.substring(0, 12)}...
                  </p>
                  <p className="text-orange-700 dark:text-orange-300">
                    <span className="font-medium">Reason:</span> {selectedRefund.reason}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Action
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setRefundAction('approve')}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
                      refundAction === 'approve'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    disabled={processing}
                  >
                    <Check size={18} className="inline mr-2" />
                    Approve
                  </button>
                  <button
                    onClick={() => setRefundAction('reject')}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
                      refundAction === 'reject'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    disabled={processing}
                  >
                    <X size={18} className="inline mr-2" />
                    Reject
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any notes about this refund decision..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                  disabled={processing}
                />
              </div>

              {refundAction === 'approve' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    ⚠️ This will process the refund immediately via Instamojo API
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3">
              <button
                onClick={closeRefundModal}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition font-semibold text-gray-700 dark:text-gray-300"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleProcessRefund}
                disabled={processing}
                className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition ${
                  refundAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Processing...
                  </span>
                ) : (
                  `${refundAction === 'approve' ? 'Approve & Process' : 'Reject'} Refund`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Orders;